import React, { useState, useEffect, useRef } from "react";
import "./ConfectionersStore.css";
import ProductCard from "../../shared/ui/ProductCard/ProductCard";

// Импортируем Firebase (как в Price.js)
import { db } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";

function ConfectionersStore({ setProduct }) {
  // Стейты для работы с БД
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const [selectedCategory, setSelectedCategory] = useState("all");
  const categoryRefs = useRef({});

  // При открытии страницы скроллим наверх
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ЗАГРУЗКА ДАННЫХ ИЗ FIREBASE
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // 1. Получаем ТОВАРЫ (из новой коллекции store_products)
        const productsSnapshot = await getDocs(collection(db, "store_products"));
        const productsData = productsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProducts(productsData);

        // 2. Получаем КАТЕГОРИИ (из новой коллекции store_categories)
        const categoriesSnapshot = await getDocs(collection(db, "store_categories"));
        const categoriesData = categoriesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        // Сортировка категорий (если нужно) и добавление кнопки "Все товары" в начало
        const sortedCategories = categoriesData.sort((a, b) => {
          if (a.slug === 'hit') return -1;
          if (b.slug === 'hit') return 1;
          return 0;
        });
        
        // Искусственно добавляем категорию "Все товары" на первое место
        setCategories([{ id: 'all', slug: 'all', name: 'Все товары' }, ...sortedCategories]);

      } catch (error) {
        console.error("Ошибка при загрузке данных магазина:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Плавный скролл категорий
  useEffect(() => {
    if (categoryRefs.current[selectedCategory]) {
      categoryRefs.current[selectedCategory].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [selectedCategory]);

  // Фильтрация товаров
  const filteredProducts = products.filter((product) => {
    if (selectedCategory === "all") return true;
    return product.category === selectedCategory;
  });

  // Обработчик кнопки "Заказать"
  const handleOrder = (product) => {
    if (typeof setProduct === "function") {
      setProduct(product);
    }
  };

  return (
    <section className="store-section" id="For_confectioners">
      
      {/* --- HERO СЕКЦИЯ (Осталась без изменений) --- */}
      <div className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <span role="img" aria-label="sparkles">✨</span>
              <span>Для профессионалов и любителей</span>
            </div>
            
            <h1 className="hero-title">
              Создавайте <span className="highlight">шедевры</span> с нашими материалами
            </h1>
            
            <p className="hero-subtitle">
              Всё для создания идеальных десертов: от проверенного инвентаря 
              до изысканного декора. Качество, которое вдохновляет.
            </p>
            
            <div className="hero-features">
              <div className="hero-feature">
                <span className="feature-icon" role="img" aria-label="target">🎯</span>
                <span>Проверено в деле</span>
              </div>
              <div className="hero-feature">
                <span className="feature-icon" role="img" aria-label="truck">🚚</span>
                <span>Быстрая доставка</span>
              </div>
              <div className="hero-feature">
                <span className="feature-icon" role="img" aria-label="heart">💝</span>
                <span>Лучшие цены</span>
              </div>
            </div>
          </div>
          
          <div className="hero-image">
            <div className="hero-image-wrapper">
              <img 
                src="https://storage.yandexcloud.net/dashacakes.store/photo/Store/person-doing-diy-activity-online-content-creation.jpg" 
                alt="Cake decorating tools" 
                className="hero-image-main"
              />
            </div>
            
            <div className="floating-card card-1">
              <div className="floating-card-icon" role="img" aria-label="trophy">🏆</div>
              <div className="floating-card-text">Премиум качество</div>
              <div className="floating-card-subtext">Мой личный выбор</div>
            </div>
          </div>
        </div>
      </div>

      {/* --- КАТАЛОГ ТОВАРОВ --- */}
      <div className="store-catalog-wrapper">
        <div className="store-header">
          <h2 className="catalog-title">Каталог товаров</h2>
        </div>

        {/* Если данные загружаются, показываем лоадер (взят из Price.css) */}
        {isLoading ? (
          <div className="price-loader" style={{ minHeight: '300px' }}>
            <div className="loader-spinner"></div>
            <p>Загружаем товары...</p>
          </div>
        ) : (
          <>
            {/* --- МЕНЮ КАТЕГОРИЙ --- */}
            <nav className="categories-nav show">
              <div className="categories-scroll">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    ref={(el) => (categoryRefs.current[cat.slug] = el)}
                    className={`category-btn ${selectedCategory === cat.slug ? "active" : ""}`}
                    onClick={() => setSelectedCategory(cat.slug)}
                  >
                    <span className="btn-text">{cat.name}</span>
                  </button>
                ))}
              </div>
            </nav>

            {/* --- СЕТКА ТОВАРОВ (PRODUCT CARDS) --- */}
            <div className="products-grid">
              {filteredProducts.length > 0 ? (
                filteredProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    id={product.id}
                    name={product.name}
                    description={product.description}
                    price={product.price}
                    old_price={product.old_price}
                    image={product.image} // Теперь сюда идет реальная ссылка из БД
                    gramm={product.quantity || product.gramm} // Поддержка обоих вариантов названия поля
                    size="" // Оставляем пустым, чтобы не выводился диаметр
                    setProduct={setProduct} // ПЕРЕДАЕМ ФУНКЦИЮ ЗАКАЗА
                    category={product.category}
                    isHit={product.isHit}
                    isNew={product.isNew}
                    collectionName="store_products"
                    likes={product.likes}
                  />
                ))
              ) : (
                <div className="no-products">
                  <span className="no-products-icon" role="img" aria-label="box">📦</span>
                  <p>В этой категории пока нет товаров</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default ConfectionersStore;